import type { ContractDocument } from "@closer/shared";
import { kimlikleriTamamla } from "@closer/shared";
import Heading from "@tiptap/extension-heading";
import Paragraph from "@tiptap/extension-paragraph";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useMemo, useRef } from "react";

import type { EditorBelgesi } from "../lib/belgeEditorModeli";
import { belgeyiEditoreCevir, editordenBelgeyeCevir } from "../lib/belgeEditorModeli";

/**
 * BEYAZ SAYFA SÖZLEŞME EDİTÖRÜ (CLOS-118'in 20 Ağustos kanonik kararı).
 *
 * ### ⚠ NEDEN VAR: karar vardı, uygulama yoktu
 *
 * CLOS-118'e 20 Ağustos 2026'da eklenen kanonik ürün kararı şunu söylüyor: *"Closer'ın
 * birincil sözleşme düzenleme deneyimi belge/sayfa odaklıdır ... kullanıcı beyaz
 * sayfa/belge yüzeyine alınır ... başlık, paragraf, kalın/italik, liste/numaralandırma,
 * hizalama kullanıcıya doğal doküman düzenleme deneyimi sunar"* ve *"bu karar farklı bir
 * birincil düzenleme modeline dönüştürülemez"*.
 *
 * Uygulamada ise düz bir `Textarea` vardı: kalın yok, başlık yok, sayfa yok. Kart yine de
 * `Done` kapatılmıştı ve CLOS-634 (yüklenen DOCX/PDF'yi aynı yüzeye aktarma) onun üstüne
 * kurulup o da `Done` kapatılmıştı. Kusuru müşteri bildirdi (4 Eylül).
 *
 * ⚠ Kökü kayda değer: içerik şemasının yorumu *"kalın/italik ... bugün hiçbir ekranın
 * üretmediği bir veri biçimi (ADR-0014)"* diyordu. O cümle 20 Ağustos kararından ÖNCESİNE
 * aitti; karar geldi, şema ve ekran güncellenmedi, kimse kabul kriterini ekranla
 * karşılaştırmadı. Testler komutu ölçüyordu, deneyimi değil.
 *
 * ### ⚠ SAKLAMA BİÇİMİ DEĞİŞMİYOR
 *
 * Editör ProseMirror belgesiyle çalışıyor ama kaydedilen şey hâlâ kanonik yapı
 * (`{ clauses: [{ id, title, blocks }] }`, ADR-0026). Çeviri `belgeEditorModeli`'nde ve
 * gidiş-dönüş kayıpsızlığı orada ölçülüyor. HTML saklamıyoruz: ADR-0026'nın gerekçeleri
 * (temizleme borcu, karşılaştırma, madde kütüphanesi) aynen geçerli.
 *
 * ### ⚠ İÇERİK DIŞARIDAN YALNIZ KİMLİK DEĞİŞİNCE YÜKLENİYOR
 *
 * `setContent` her render'da çağrılsaydı kullanıcı yazarken imleç her tuşta belgenin
 * başına atlardı. Editör kendi durumunun sahibi; dışarıdan yükleme yalnız BAŞKA bir
 * sözleşmeye geçildiğinde yapılıyor.
 */

/**
 * MADDE SINIRINI TAŞIYAN BAŞLIK.
 *
 * ⚠ `clauseId` bir ÖZNİTELİK olmak zorunda: madde sınırını başlık SEVİYESİYLE ayırt
 * etseydik, içe aktarılan bir belgedeki 1. seviye ara başlık sessizce yeni madde açardı
 * (gerekçenin tamamı `belgeEditorModeli.ts`'te, bekçisi de orada).
 *
 * ⚠ `data-clause-id` olarak HTML'e de yazılıyor: TipTap içeriği HTML üzerinden yeniden
 * ayrıştırabiliyor (yapıştırma, geri alma) ve öznitelik yalnız JSON'da yaşasaydı o
 * yollarda kimlik düşerdi.
 */
/**
 * BLOK KİMLİĞİ ÖZNİTELİĞİ (CLOS-1233 — Kanon §11).
 *
 * ⚠ `clauseId` ile AYNI gerekçe, bir düzey aşağıda: yorum çapası, müzakere hedefi,
 * revizyon farkı ve AI bulgusu bloğa tutunuyor. Konuma tutunsalardı, kullanıcı öne bir
 * paragraf eklediği anda hepsi sessizce başka bir metnin altına düşerdi.
 *
 * ⚠ `data-block-id` olarak HTML'e de yazılıyor: TipTap içeriği HTML üzerinden yeniden
 * ayrıştırabiliyor (yapıştırma, geri alma) ve öznitelik yalnız JSON'da yaşasaydı o
 * yollarda kimlik düşerdi — yani tam da kimliğin en çok gerektiği anlarda.
 */
const blokKimligi = {
  blockId: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute("data-block-id"),
    renderHTML: (attributes: Record<string, unknown>) =>
      attributes["blockId"] ? { "data-block-id": String(attributes["blockId"]) } : {},
  },
};

const MaddeBasligi = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      clauseId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-clause-id"),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes["clauseId"] ? { "data-clause-id": String(attributes["clauseId"]) } : {},
      },
      ...blokKimligi,
    };
  },
});

const KimlikliParagraf = Paragraph.extend({
  addAttributes() {
    return { ...this.parent?.(), ...blokKimligi };
  },
});

export function ContractDocumentEditor({
  belge,
  duzenlenebilir,
  onChange,
  contractId,
}: {
  belge: ContractDocument;
  duzenlenebilir: boolean;
  onChange: (belge: ContractDocument) => void;
  /** ⚠ Yükleme anahtarı: sözleşme değişince içerik yeniden basılıyor, yazarken değil. */
  contractId: string;
}) {
  /**
   * ⚠ SON YAYILAN BELGE. `onChange` yalnız belge GERÇEKTEN değiştiğinde çağrılıyor.
   *
   * Ölçüldü: editör montajda bir güncelleme olayı yayıyor (yalnız `setContent`'in
   * `emitUpdate: false` bayrağı yetmedi) ve sayfa hiç kimse yazmadan taslağı KİRLİ
   * sayıyordu. Somut sonucu "Versiyon üret" düğmesinin açılışta kilitli kalmasıydı ve
   * mevcut bir test bunu yakaladı. Olayın kaynağını kovalamak yerine sözleşmeyi
   * netleştirdik: aynı belge iki kez yayılmaz.
   */
  /**
   * ⚠ KİMLİKSİZ BELGE YÜKLENİRKEN TAMAMLANIYOR (CLOS-1233).
   *
   * CLOS-1233 öncesi kaydedilmiş belgelerin bloklarında kimlik yok ve `contract_versions`
   * yeniden yazılmıyor (kanonun 1. kuralı). Ham hâliyle yükleseydik ilk gidiş-dönüş
   * bloklara kimlik ekler, sonuç yüklenen belgeden farklı çıkar ve editör HİÇ KİMSE
   * YAZMADAN "değişiklik" yayardı. Bunun somut bedeli ölçülmüştü: "Versiyon üret" düğmesi
   * açılışta kilitli kalıyordu. Kimlik yüklemede tamamlanınca taban ile editörün ürettiği
   * belge aynı uzayda oluyor.
   */
  const kimlikliBelge = useMemo(() => kimlikleriTamamla(belge), [belge]);

  /**
   * ⚠ TABAN, editörün O BELGE İÇİN ÜRETECEĞİ şeyden hesaplanıyor — belgenin kendisinden
   * değil.
   *
   * `JSON.stringify` anahtar SIRASINA duyarlı: `{...blok, id}` ile `{kind, text, id}` aynı
   * belgeyi anlatsa da farklı dizge üretir. Tabanı ham belgeden alsaydık editör montajda
   * "değişiklik" yayardı; hiç kimse yazmadan taslak KİRLİ sayılır ve "Versiyon üret"
   * düğmesi açılışta kilitli kalırdı. Bu kusur bir kez yaşandı ve `montajda değişiklik
   * YAYMIYOR` testi onu yeniden yakaladı.
   *
   * ⚠ Üretici burada asla çağrılmıyor: `kimlikliBelge`'nin her bloğunda kimlik var.
   */
  const editorunUretecegi = (b: ContractDocument) =>
    JSON.stringify(editordenBelgeyeCevir(belgeyiEditoreCevir(b), () => crypto.randomUUID()));

  const sonYayilan = useRef<string>(editorunUretecegi(kimlikliBelge));

  const editor = useEditor({
    editable: duzenlenebilir,
    /**
     * ⚠ ERİŞİLEBİLİR AD VE ROL AÇIKÇA VERİLİYOR. ProseMirror yalnız `contenteditable` bir
     * div basıyor; adsız bırakırsak ekran okuyucu kullanıcısı yüzeyin ne olduğunu duymaz
     * ve testler de onu adıyla bulamaz. `aria-multiline` şart: tek satırlık bir metin
     * kutusu sanılırsa gezinme tuşları farklı okunur.
     */
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": "Sözleşme metni",
        class: "closer-belge",
      },
    },
    extensions: [
      StarterKit.configure({
        /**
         * ⚠ Kanonik modelin taşımadığı yapılar KAPALI. Açık bıraksaydık kullanıcı alıntı
         * ya da kod bloğu yazabilir, kaydetmede sessizce düz paragrafa dönerdi —
         * "yazdığım gitti" kusuru.
         */
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        link: false,
        /** ⚠ Kendi başlığımızı kullanıyoruz: `clauseId` özniteliği onda. */
        heading: false,
        /** ⚠ Kendi paragrafımızı kullanıyoruz: `blockId` özniteliği onda (CLOS-1233). */
        paragraph: false,
      }),
      MaddeBasligi.configure({ levels: [1, 2, 3] }),
      KimlikliParagraf,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: belgeyiEditoreCevir(kimlikliBelge) as unknown as Record<string, unknown>,
    onUpdate: ({ editor: aktif }) => {
      const json = aktif.getJSON() as unknown as EditorBelgesi;
      /**
       * ⚠ Kimlik artık DÜĞÜMÜN ÖZNİTELİĞİNDEN geliyor (CLOS-1233).
       *
       * Buradaki eski satır `kimlikler.current[i]` idi — kimliği SIRAYA göre dağıtıyordu.
       * Amacı doğruydu ("her tuş vuruşunda yeni kimlik üretme") ama yöntemi kanonun
       * yasakladığı davranışı üretiyordu: öne bir madde eklendiğinde alttaki her madde bir
       * öncekinin kimliğini devralıyor, o maddeye asılı yorum/müzakere/revizyon sessizce
       * başka bir metne kayıyordu. Üretici artık yalnız GERÇEKTEN YENİ düğümler için
       * çağrılıyor.
       */
      const sonraki = editordenBelgeyeCevir(json, () => crypto.randomUUID());
      const imza = JSON.stringify(sonraki);
      if (imza === sonYayilan.current) return;
      sonYayilan.current = imza;
      onChange(sonraki);
    },
  });

  useEffect(() => {
    if (!editor) return;
    /**
     * ⚠ `emitUpdate: false` ZORUNLU ve bedeli ölçüldü. Varsayılan davranışta `setContent`
     * bir güncelleme olayı yayıyor; `onUpdate` çalışıyor, sayfa taslağı KİRLİ sayıyor ve
     * kullanıcı hiçbir şey yazmadan "kaydedilmemiş değişiklik" durumuna düşüyor. Somut
     * sonucu: "Versiyon üret" düğmesi açılışta kilitli kalıyordu — mevcut bir test
     * (`metinden versiyon üretme`) bunu yakaladı.
     */
    sonYayilan.current = editorunUretecegi(kimlikliBelge);
    editor.commands.setContent(
      belgeyiEditoreCevir(kimlikliBelge) as unknown as Record<string, unknown>,
      {
        emitUpdate: false,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yükleme yalnız sözleşme değişince
  }, [contractId, editor]);

  useEffect(() => {
    editor?.setEditable(duzenlenebilir);
  }, [duzenlenebilir, editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-3">
      {duzenlenebilir ? <BicimCubugu editor={editor} /> : null}

      {/*
        ⚠ BEYAZ SAYFA. Kartın istediği şey bir metin kutusu değil, belge yüzeyi: beyaz
        zemin, sayfa genişliği ve kenar boşlukları. Genişlik `max-w-[820px]` — A4'ün ekranda
        okunur karşılığı; sınırsız bıraksaydık satırlar geniş ekranda okunamayacak kadar
        uzardı.
      */}
      <div className="overflow-x-auto rounded-md border border-border-default bg-muted p-4 sm:p-8">
        <div className="mx-auto w-full max-w-[820px] bg-surface px-6 py-10 shadow-sm sm:px-16 sm:py-16">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}

/** Biçimlendirme çubuğu — kartın saydığı yetenekler ve yalnız onlar. */
function BicimCubugu({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) {
  /**
   * ⚠ MADDE BAŞLIĞI DÜZ METİN — karakter biçimi orada TEKLİF EDİLMİYOR (BUG-017).
   *
   * Kullanıcı başlığı kalın yapabiliyordu ama kaydedince biçim yok oluyordu; aynı işlem
   * paragrafta çalışıyordu. Sebep kanonik modelde: `title: z.string()` işaret taşımıyor,
   * `marks` yalnız bloklarda yaşıyor — çeviri de onları düşürüyordu (`belgeEditorModeli`).
   *
   * ⚠ MODEL GENİŞLETİLMEDİ ve bu bilinçli: ADR-0026 "kanonik saklama biçimi yapısal ve
   * öyle kalıyor" diyor; kayıtlı bir mimari kararı kusur düzeltmesiyle sessizce devirmek
   * yerine ürün DÜRÜST hâle getirildi. Saklanamayacak bir biçim artık sunulmuyor.
   *
   * ⚠ Yol KAPANMIYOR: ara başlık (H2) ve listeler açık kalıyor. Zengin metin isteyen
   * kullanıcı madde İÇİ ara başlığa yönlendiriliyor — o blok işaretleri taşıyor.
   */
  const maddeBasligindaMi = editor.isActive("heading", { level: 1 });
  const KARAKTER_BICIMLERI = new Set(["Kalın", "İtalik", "Altı çizili"]);
  const SEBEP = "Madde başlığı düz metindir; biçimlendirme için madde içi ara başlık kullanın";

  const dugmeler: { etiket: string; baslik: string; aktif: boolean; calistir: () => void }[] = [
    {
      etiket: "B",
      baslik: "Kalın",
      aktif: editor.isActive("bold"),
      calistir: () => editor.chain().focus().toggleBold().run(),
    },
    {
      etiket: "I",
      baslik: "İtalik",
      aktif: editor.isActive("italic"),
      calistir: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      etiket: "U",
      baslik: "Altı çizili",
      aktif: editor.isActive("underline"),
      calistir: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      etiket: "H2",
      baslik: "Ara başlık",
      aktif: editor.isActive("heading", { level: 2 }),
      calistir: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      etiket: "•",
      baslik: "Madde işaretli liste",
      aktif: editor.isActive("bulletList"),
      calistir: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      etiket: "1.",
      baslik: "Numaralı liste",
      aktif: editor.isActive("orderedList"),
      calistir: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      etiket: "≡",
      baslik: "İki yana yasla",
      aktif: editor.isActive({ textAlign: "justify" }),
      calistir: () => editor.chain().focus().setTextAlign("justify").run(),
    },
  ];

  return (
    /*
      ⚠ `role="toolbar"` ve her düğmede `aria-pressed`: ekran okuyucu kullanıcısı hangi
      biçimin AÇIK olduğunu ancak böyle duyar. Yalnız görsel vurgu bırakmak, biçimi
      göremeyen kullanıcı için düğmeyi durumsuz bir tetikleyiciye indirirdi.
    */
    <div role="toolbar" aria-label="Metin biçimlendirme" className="flex flex-wrap gap-1">
      {dugmeler.map((dugme) => {
        const kapali = maddeBasligindaMi && KARAKTER_BICIMLERI.has(dugme.baslik);
        return (
          <button
            key={dugme.baslik}
            type="button"
            /** ⚠ Devre dışı düğme SEBEBİNİ söylüyor: sessizce tıklanamamak, sessizce
              kaybolan biçim kadar kötü olurdu. */
            title={kapali ? SEBEP : dugme.baslik}
            aria-label={dugme.baslik}
            aria-pressed={dugme.aktif}
            disabled={kapali}
            onClick={dugme.calistir}
            className={`min-w-9 rounded-md border px-2.5 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              dugme.aktif
                ? "border-primary bg-primary-soft text-primary"
                : "border-border-default bg-surface text-fg hover:bg-muted"
            }`}
          >
            {dugme.etiket}
          </button>
        );
      })}
    </div>
  );
}
